import React, { useEffect, useState } from "react";

import {
  BellIcon as Bell,
  InfoSmIcon as InfoSm,
  Typography
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";

import { IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import {
  getBillingAlert,
  billingAlertSelector,
  billingAlertThresholdSelector,
  isBillingAlertLoadingSelector
} from "Reducers/organization/billingAlert";
import {
  selectOrganizationSubscriptions,
  loadingListSelector,
  loadSubscriptions
} from "Reducers/organization/subscription";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import EstimateDialog from "src/organization/pages/billing/components/Overview/EstimateDialog";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import BillingAlertsModal from "../BillingAlertsModal";
import CurrentSpendDetails from "../CurrentSpendDetails";
import PaymentMethodPanel from "../PaymentMethodPanel";
import ProjectRow from "../ProjectRow";
import SCASticker from "../SCASticker/SCASticker";

import * as S from "./OrganizationBreakdown.style";

import type {
  OrganizationEstimateComplex,
  OrganizationOrder
} from "@packages/client";

interface OrganizationBreakdownProps {
  estimateCurrent?: OrganizationEstimateComplex;
  estimateFuture?: OrganizationEstimateComplex;
  order?: OrganizationOrder;
  asOfOrder?: OrganizationOrder;
  isTrial?: boolean;
}

const OrganizationBreakdown = ({
  order,
  asOfOrder,
  estimateCurrent,
  estimateFuture,
  isTrial
}: OrganizationBreakdownProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [showBillingAlertsModal, setShowBillingAlertsModal] = useState(false);
  const [checkForSubscriptions, setCheckForSubscriptions] = useState(true);
  const [displayProjection, setDisplayProjection] = useState<
    "current" | "future"
  >("current");

  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const orgSubscriptions = useSelectorWithUrlParams(
    selectOrganizationSubscriptions
  );
  const subscriptionsLoading = useAppSelector(loadingListSelector);

  const [organizationUsersUrl] = useUrls("organization.settings.users");

  const billingAlert = useSelectorWithUrlParams(billingAlertSelector);
  const billingAlertThreshold = useSelectorWithUrlParams(
    billingAlertThresholdSelector
  );
  const isBillingAlertLoading = useSelectorWithUrlParams(
    isBillingAlertLoadingSelector
  );

  useEffect(() => {
    if (organization && !subscriptionsLoading && checkForSubscriptions) {
      setCheckForSubscriptions(false);
      dispatch(loadSubscriptions({ organizationId: organization.name }));
    }
  }, [dispatch, organization, subscriptionsLoading, checkForSubscriptions]);

  useEffect(() => {
    if (!billingAlert) {
      dispatch(getBillingAlert({ organizationId }));
    }
  }, [dispatch, organizationId, billingAlert]);

  /*
  const greenDiscount = order
    ? Object.values(order.components).find(orderComponent =>
        orderComponent.display_title.toLowerCase().includes("green discount")
      )
    : null;
  */

  const estimate =
    displayProjection === "current" ? estimateCurrent : estimateFuture;

  if (!estimate) return null;

  const viewerUsers = estimate.user_licenses.base?.list?.viewer_user;
  const adminUsers = estimate.user_licenses.base?.list?.admin_user;
  const pricePerUser = estimate.options?.admin_user?.formatted;
  const usersTotalPrice = estimate.user_licenses.total?.formatted;
  const userManagementTotal =
    estimate.user_licenses.user_management?.total?.formatted;
  const userManagementCount = estimate.user_licenses.user_management?.count;
  const userManagementPerUserPrice =
    estimate.options?.standard_management_user?.formatted;
  const subscriptions = estimate.subscriptions;
  const supportLevelPrice = estimate.support_level.formatted;
  const supportLevel =
    estimate.support_level.sku
      ?.replace("UPSUN-FEATURE-SUPPORT", "")
      .replace("-", "")
      .toLowerCase() || "standard";
  const onboardingTraining = estimate.onboarding_training;
  const onboardingTrainingUnitPrice =
    estimate.options?.onboarding_training?.formatted;
  const vouchers = estimate.vouchers?.formatted;

  const discounts = [
    {
      ...estimate.allowance_discount,
      name: isTrial ? "allowance_discount.trial" : "allowance_discount"
    },
    { ...estimate.enterprise_discount, name: "enterprise_discount" },
    { ...estimate.startup_discount, name: "startup_discount" }
  ];

  const subtotal = estimate.sub_total?.formatted;
  const totalCurrentMonth = estimateCurrent!.total.formatted;

  const lastRefreshed = order
    ? moment(order.last_refreshed).format("h:mma MMM D YYYY")
    : null;

  const projectList = subscriptionsLoading ? (
    <S.LoadingProject />
  ) : subscriptions?.list.length ? (
    <>
      {subscriptions.list.map(({ license_id, project_title, total }) => {
        const subscription = orgSubscriptions.find(
          sub => sub?.id === `${license_id}`
        );

        if (!subscription) return null;

        return (
          <ProjectRow
            greenDiscount={subscription.green}
            key={license_id}
            title={project_title}
            price={total.formatted}
            organizationId={organizationId}
            projectId={subscription.project_id}
            subscriptionId={subscription.id}
          />
        );
      })}
    </>
  ) : null;

  return (
    <>
      <O.ContentWrapper>
        <O.InnerWrapper>
          <O.Stack>
            <S.OrganizationTitleWrapper>
              <Typography.Title tag="h2">
                {intl.formatMessage(
                  {
                    id: "organization.billing.breakdown.title",
                    defaultMessage: `${organization?.label || ""} billing`
                  },
                  {
                    name: organization?.label || ""
                  }
                )}
              </Typography.Title>
              {isBillingAlertLoading ? (
                <S.LoadingAlertBtn />
              ) : (
                <S.AlertButton
                  onClick={() => setShowBillingAlertsModal(true)}
                  analyticId="organization.billing.breakdown.current.alert"
                >
                  <Bell />
                  <span>
                    {billingAlertThreshold
                      ? intl.formatMessage(
                          {
                            id: "organization.billing.breakdown.current.alert"
                          },
                          { alertThreshold: billingAlertThreshold?.formatted }
                        )
                      : intl.formatMessage({
                          id: "organization.billing.breakdown.set.alert",
                          defaultMessage: "Set alert"
                        })}
                  </span>
                </S.AlertButton>
              )}

              <SCASticker />

              <CurrentSpendDetails
                currentValue={asOfOrder?.total}
                currentFormatted={asOfOrder?.total_formatted ?? ""}
                projectedValue={estimateCurrent!.total.amount}
                projectedFormatted={totalCurrentMonth}
              />

              <PaymentMethodPanel organization={organization} />
            </S.OrganizationTitleWrapper>
            <O.PanelWrapper>
              <O.Stack space={32}>
                <div>
                  <O.InvoiceTitleWrapper>
                    <O.InvoiceNextTitle tag="h3">
                      {intl.formatMessage({
                        id:
                          displayProjection == "future"
                            ? "organization.billing.future_months_projection"
                            : "organization.billing.this_months_projection"
                      })}
                    </O.InvoiceNextTitle>
                    <O.BodyTextSubtle>
                      {lastRefreshed &&
                        intl.formatMessage(
                          {
                            id: "organization.billing.last_update_at_value"
                          },
                          { value: lastRefreshed }
                        )}
                    </O.BodyTextSubtle>
                    <O.ToggleWrapper>
                      <O.Toggle
                        onPressedChange={() => setDisplayProjection("current")}
                        text={intl.formatMessage({
                          id: "organization.billing.this_month"
                        })}
                        toggleType="text"
                        pressed={displayProjection === "current"}
                      />
                      <O.Toggle
                        onPressedChange={() => setDisplayProjection("future")}
                        text={intl.formatMessage({
                          id: "organization.billing.future"
                        })}
                        toggleType="text"
                        pressed={displayProjection === "future"}
                      />
                    </O.ToggleWrapper>
                  </O.InvoiceTitleWrapper>
                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "organization.billing.invoice.description"
                    })}
                  </Typography.BodyText>
                </div>

                <S.UsersTable>
                  <O.RowLabel>
                    {intl.formatMessage({
                      id: "organization.billing.users",
                      defaultMessage: "Users"
                    })}
                    <S.ManageUsersBtn variant="link" to={organizationUsersUrl}>
                      {intl.formatMessage({
                        id: "organization.billing.breakdown.manage_users",
                        defaultMessage: "Manage users"
                      })}
                    </S.ManageUsersBtn>
                  </O.RowLabel>

                  <O.ColumnHeading>
                    {intl.formatMessage({
                      id: "organization.billing.type"
                    })}
                  </O.ColumnHeading>
                  <O.ColumnHeading>
                    {intl.formatMessage({
                      id: "organization.billing.unit_price"
                    })}
                  </O.ColumnHeading>
                  <O.ColumnHeading>
                    {intl.formatMessage({
                      id: "organization.billing.quantity"
                    })}
                  </O.ColumnHeading>
                  <O.ColumnHeading>
                    {displayProjection === "current" ? (
                      <O.DialogWrapper>
                        {intl.formatMessage({
                          id: "organization.billing.current_spend"
                        })}{" "}
                        <Dialog
                          analyticId="organization.billing.prorated_quantity.description"
                          title={intl.formatMessage({
                            id: "organization.billing.current_spend"
                          })}
                          description={intl.formatMessage({
                            id: "organization.billing.current_spend.description"
                          })}
                          trigger={
                            <IconControl aria-label="info-trigger-billing">
                              <InfoSm />
                            </IconControl>
                          }
                        />
                      </O.DialogWrapper>
                    ) : (
                      ""
                    )}
                  </O.ColumnHeading>
                  <O.ColumnHeading>
                    <O.DialogWrapper justifyContent="end">
                      {intl.formatMessage({
                        id: "organization.billing.projected_total"
                      })}
                      <Dialog
                        analyticId={`organization.billing.projected_total.description.${displayProjection}`}
                        align="end"
                        title={intl.formatMessage({
                          id: `organization.billing.projected_total.title.${displayProjection}`
                        })}
                        description={intl.formatMessage({
                          id: `organization.billing.projected_total.description.${displayProjection}`
                        })}
                        trigger={
                          <IconControl aria-label="info-trigger-projected_total">
                            <InfoSm />
                          </IconControl>
                        }
                      />
                    </O.DialogWrapper>
                  </O.ColumnHeading>

                  <O.Separator type="minor" />

                  {adminUsers?.total && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "organization.billing.breakdown.user_licenses"
                        })}
                      </Typography.BodyText>
                      <O.BodyTextSubtle>{pricePerUser}</O.BodyTextSubtle>
                      <O.BodyTextSubtle>
                        {adminUsers.total.current_usage}
                      </O.BodyTextSubtle>
                      <O.BodyTextSubtle>
                        {displayProjection === "current"
                          ? (asOfOrder?.line_items.find(
                              item => item.product === "User licenses"
                            )?.total_price_formatted ??
                            adminUsers.total.formatted)
                          : ""}
                      </O.BodyTextSubtle>
                      <O.RightText>{adminUsers.total.formatted}</O.RightText>
                    </>
                  )}

                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "organization.billing.user_management"
                    })}
                  </Typography.BodyText>
                  <O.BodyTextSubtle>
                    {userManagementPerUserPrice}
                  </O.BodyTextSubtle>
                  <O.BodyTextSubtle>{userManagementCount}</O.BodyTextSubtle>
                  <O.BodyTextSubtle>
                    {displayProjection === "current"
                      ? (asOfOrder?.line_items.find(
                          item => item.product === "Standard user management"
                        )?.total_price_formatted ?? userManagementTotal)
                      : ""}
                  </O.BodyTextSubtle>
                  <O.RightText>{userManagementTotal}</O.RightText>

                  {viewerUsers?.total && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "organization.billing.breakdown.viewers",
                          defaultMessage: "Viewers"
                        })}
                      </Typography.BodyText>
                      <O.BodyTextSubtle>
                        {intl.formatMessage({
                          id: "organization.billing.free",
                          defaultMessage: "Free"
                        })}
                      </O.BodyTextSubtle>
                      <O.BodyTextSubtle>
                        {viewerUsers.total.current_usage}
                      </O.BodyTextSubtle>
                      <O.BodyTextSubtle>
                        {displayProjection === "current"
                          ? viewerUsers.total.formatted
                          : ""}
                      </O.BodyTextSubtle>
                      <O.RightText>{viewerUsers.total.formatted}</O.RightText>
                    </>
                  )}

                  <O.Separator type="minor" />

                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "organization.billing.users",
                      defaultMessage: "Users"
                    })}
                  </Typography.BodyText>
                  <S.Total tag="h3">{usersTotalPrice}</S.Total>
                </S.UsersTable>

                {projectList && (
                  <S.ProjectsTable>
                    <O.RowLabel>
                      {intl.formatMessage({
                        id: "organization.billing.projects",
                        defaultMessage: "Projects"
                      })}
                    </O.RowLabel>

                    {projectList}

                    <O.Separator type="minor" />

                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "organization.billing.projects",
                        defaultMessage: "Projects"
                      })}
                    </Typography.BodyText>
                    <S.Total tag="h3">
                      {subscriptions?.total?.formatted}
                    </S.Total>
                  </S.ProjectsTable>
                )}

                <S.SupportTable>
                  <O.RowLabel>
                    {intl.formatMessage({
                      id: "organization.billing.breakdown.support",
                      defaultMessage: "Support"
                    })}
                  </O.RowLabel>

                  {supportLevelPrice && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: `organization.billing.breakdown.support_sla.${supportLevel}`
                        })}
                      </Typography.BodyText>
                      <O.BodyTextSubtle weight="regular">
                        {intl.formatMessage({
                          id: `organization.billing.modal.org_support.${supportLevel}.detail`
                        })}
                      </O.BodyTextSubtle>
                      <O.RightText>{supportLevelPrice}</O.RightText>
                    </>
                  )}

                  <O.Separator type="minor" />

                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "organization.billing.breakdown.support",
                      defaultMessage: "Support"
                    })}
                  </Typography.BodyText>
                  <S.Total tag="h3">{supportLevelPrice}</S.Total>
                </S.SupportTable>

                {onboardingTraining && onboardingTraining.current_usage > 0 && (
                  <S.OtherTable>
                    <O.RowLabel>
                      {intl.formatMessage({
                        id: "organization.billing.other"
                      })}
                    </O.RowLabel>

                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "organization.billing.onboarding_training"
                      })}
                    </Typography.BodyText>
                    <O.BodyTextSubtle>
                      {onboardingTrainingUnitPrice}
                    </O.BodyTextSubtle>
                    <O.BodyTextSubtle>
                      {onboardingTraining.current_usage}
                    </O.BodyTextSubtle>
                    <O.BodyTextSubtle>
                      {onboardingTraining.formatted}
                    </O.BodyTextSubtle>
                    <O.RightText>{onboardingTraining.formatted}</O.RightText>
                  </S.OtherTable>
                )}

                <S.SubtotalTable>
                  <O.Separator type="major" />
                  {subtotal && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "organization.billing.subtotal",
                          defaultMessage: "Subtotal"
                        })}
                      </Typography.BodyText>
                      <div />
                      <O.RightText>{subtotal}</O.RightText>
                    </>
                  )}
                  {vouchers && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "organization.billing.voucher",
                          defaultMessage: "Vouchers"
                        })}
                      </Typography.BodyText>
                      <div />
                      <O.RightText>{vouchers}</O.RightText>
                    </>
                  )}
                  {discounts.map(discount => {
                    return discount.amount ? (
                      <React.Fragment key={discount.name}>
                        <Typography.BodyText>
                          {intl.formatMessage({
                            id: `organization.billing.discount.${discount.name}`
                          })}
                        </Typography.BodyText>
                        <div />
                        <O.RightText>{discount.formatted}</O.RightText>
                      </React.Fragment>
                    ) : null;
                  })}

                  {/*
                  {!!greenDiscount?.amount && (
                    <>
                      <S.TitleIconWrapper>
                        <Typography.BodyText>{greenDiscount.display_title}</Typography.BodyText>
                        <Dialog
                          trigger={
                            <O.IconWrapper>
                              <Leaf
                                color={"var(--icon-java-fill,var(--java))"}
                                width="24"
                                height="24"
                              />
                            </O.IconWrapper>
                          }
                          title={intl.formatMessage({
                            id: "green_discount.title"
                          })}
                          description={intl.formatMessage({
                            id: "green_discount.description"
                          })}
                        />
                      </S.TitleIconWrapper>
                      <O.BodyTextSubtle>
                        {intl.formatMessage({
                          id: "billing.invoice.project_green_discount_description",
                          defaultMessage:
                            "3% of CPU, memory, and build resources"
                        })}
                      </O.BodyTextSubtle>
                      <O.RightText>
                        {formatAsCurrency(
                          greenDiscount.amount,
                          greenDiscount.currency
                        )}
                      </O.RightText>
                    </>
                  )}
                  */}
                </S.SubtotalTable>

                <O.Estimate
                  variant={displayProjection === "current" ? "default" : "dark"}
                >
                  <EstimateDialog
                    analyticId={`organization.billing.projected_total.description.${displayProjection}`}
                    label={intl.formatMessage({
                      id: `organization.billing.projected_total.title.${displayProjection}`
                    })}
                    tooltipTitle={intl.formatMessage({
                      id: `organization.billing.projected_total.title.${displayProjection}`
                    })}
                    tooltipDescription={intl.formatMessage({
                      id: `organization.billing.projected_total.description.${displayProjection}`
                    })}
                    total={estimate.total.formatted}
                    description={
                      isTrial && displayProjection === "current"
                        ? intl.formatMessage({
                            id: "organization.billing.trial_discount_not_included"
                          })
                        : undefined
                    }
                  />
                </O.Estimate>
              </O.Stack>
            </O.PanelWrapper>
          </O.Stack>
        </O.InnerWrapper>
      </O.ContentWrapper>
      {showBillingAlertsModal && (
        <BillingAlertsModal
          showBillingAlertsModal={showBillingAlertsModal}
          setShowBillingAlertsModal={setShowBillingAlertsModal}
        />
      )}
    </>
  );
};

export default OrganizationBreakdown;
